import pick from 'lodash/pick';
import { freeTransactionCreate, getListingVariants, getShippingRates, initiatePrivileged, onSendOutOfStockMailProviders, onSingleMailApiCollection, transitionPrivileged, updateVariantStock } from '../../util/api';
import { denormalisedResponseEntities, makeFreeTransactionData } from '../../util/data';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { fetchCurrentUserHasOrdersSuccess, fetchCurrentUser, fetchAllUserTransactions, fetchCurrentUserGroups, updatedVisitorData } from '../../ducks/user.duck';
import {
  algoliaEventTrackFn,
  PURCHASE_COMPLETED_EVENT,
  purchasedObjectIDsMethodName
} from '../AlgoliaSearchPage/AlgoliaEvents';
import { queryUserListings } from '../ProfilePage/ProfilePage.duck';
import { PURCHASE_FREE_PROCESS_NAME } from '../../transactions/transaction';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/CheckoutPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/CheckoutPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/CheckoutPage/SPECULATE_TRANSACTION_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

export const INITIATE_INQUIRY_REQUEST = 'app/CheckoutPage/INITIATE_INQUIRY_REQUEST';
export const INITIATE_INQUIRY_SUCCESS = 'app/CheckoutPage/INITIATE_INQUIRY_SUCCESS';
export const INITIATE_INQUIRY_ERROR = 'app/CheckoutPage/INITIATE_INQUIRY_ERROR';

export const FETCH_SHIPPING_RATES_REQUEST = 'app/CheckoutPage/FETCH_SHIPPING_RATES_REQUEST';
export const FETCH_SHIPPING_RATES_SUCCESS = 'app/CheckoutPage/FETCH_SHIPPING_RATES_SUCCESS';
export const FETCH_SHIPPING_RATES_ERROR = 'app/CheckoutPage/FETCH_SHIPPING_RATES_ERROR';

export const FETCH_SHIPPING_LINE_ITEM_REQUEST = 'app/CheckoutPage/FETCH_SHIPPING_LINE_ITEM_REQUEST';

const getUuidValue = value => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (value.uuid) return value.uuid;
  if (value.id?.uuid) return value.id.uuid;
  if (typeof value.toString === 'function') {
    const stringValue = value.toString();
    const uuidMatch = stringValue.match(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
    );
    return uuidMatch ? uuidMatch[0] : stringValue !== '[object Object]' ? stringValue : null;
  }
  return null;
};

// ================ Reducer ================ //

const initialState = {
  listing: null,
  orderData: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  isClockInSync: false,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  initiateInquiryInProgress: false,
  initiateInquiryError: null,
  fetchRatesInProgress: false,
  shippingRates: [],
  fetchRatesError: null,
  cartListings: null,
  cartAuthorId: null,
  shippingLineItemRequest: null,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
        speculatedTransaction: null,
      };
    case SPECULATE_TRANSACTION_SUCCESS: {
      // Check that the local devices clock is within a minute from the server
      const lastTransitionedAt = payload.transaction?.attributes?.lastTransitionedAt;
      const localTime = new Date();
      const minute = 60000;
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculatedTransaction: payload.transaction,
        isClockInSync: Math.abs(lastTransitionedAt?.getTime() - localTime.getTime()) < minute,
        shippingLineItemRequest: false
      };
    }
    case SPECULATE_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: payload,
        shippingLineItemRequest: false
      };

    case FETCH_SHIPPING_LINE_ITEM_REQUEST:
      return {
        ...state,
        shippingLineItemRequest: true,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, confirmPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };

    case INITIATE_INQUIRY_REQUEST:
      return { ...state, initiateInquiryInProgress: true, initiateInquiryError: null };
    case INITIATE_INQUIRY_SUCCESS:
      return { ...state, initiateInquiryInProgress: false };
    case INITIATE_INQUIRY_ERROR:
      return { ...state, initiateInquiryInProgress: false, initiateInquiryError: payload };

    case FETCH_SHIPPING_RATES_REQUEST:
      return { ...state, fetchRatesInProgress: true, fetchRatesError: null, shippingRates: [] };
    case FETCH_SHIPPING_RATES_SUCCESS:
      return { ...state, fetchRatesInProgress: false, fetchRatesError: null, shippingRates: payload };
    case FETCH_SHIPPING_RATES_ERROR:
      return { ...state, fetchRatesInProgress: false, fetchRatesError: payload, shippingRates: [] };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = order => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: order,
});

const initiateOrderError = e => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = orderId => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = e => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = () => ({ type: SPECULATE_TRANSACTION_REQUEST });

export const speculateTransactionSuccess = transaction => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});

export const speculateTransactionError = e => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

export const initiateInquiryRequest = () => ({ type: INITIATE_INQUIRY_REQUEST });
export const initiateInquirySuccess = () => ({ type: INITIATE_INQUIRY_SUCCESS });
export const initiateInquiryError = e => ({
  type: INITIATE_INQUIRY_ERROR,
  error: true,
  payload: e,
});

export const fetchRatesRequest = () => ({ type: FETCH_SHIPPING_RATES_REQUEST });
export const fetchRatesSuccess = (response) => ({ type: FETCH_SHIPPING_RATES_SUCCESS, payload: response });
export const fetchRatesError = e => ({
  type: FETCH_SHIPPING_RATES_ERROR,
  error: true,
  payload: e,
});

export const fetchShippingLineItemRequest = () => ({ type: FETCH_SHIPPING_LINE_ITEM_REQUEST });

/* ================ Thunks ================ */

const getCartListingIds = cart =>
  Object.keys(cart || {}).filter(listingId => listingId !== 'deliveryMethod');

const getCartItemQuantity = cartItem => Number(cartItem?.count || cartItem?.quantity || 0);

const getListingUuid = listing => listing?.id?.uuid || listing?.id;

const getPlainListingStockStatus = ({ listingId, cartItem, listing }) => {
  const requestedQuantity = getCartItemQuantity(cartItem);
  const currentStock = listing?.currentStock?.attributes?.quantity;

  if (typeof currentStock !== 'number') {
    return { isAvailable: true };
  }

  if (currentStock <= 0) {
    return {
      isAvailable: false,
      reason: 'out_of_stock',
      listingId,
      variantSku: null,
      variantLabel: null,
      requestedQuantity,
      currentStock: 0,
    };
  }

  if (currentStock < requestedQuantity) {
    return {
      isAvailable: false,
      reason: 'insufficient_stock',
      listingId,
      variantSku: null,
      variantLabel: null,
      requestedQuantity,
      currentStock,
    };
  }

  return { isAvailable: true };
};

const createCartStockValidationError = invalidItems => {
  const error = new Error('Some cart items are no longer available with the selected stock or variant.');
  error.name = 'CartStockValidationError';
  error.invalidItems = invalidItems;
  return error;
};

const getVariantStockStatus = ({ listingId, cartItem, variants }) => {
  const variantSku = cartItem?.variantSku || cartItem?.sku;
  const variantLabel = cartItem?.variantLabel || null;
  const requestedQuantity = getCartItemQuantity(cartItem);
  const hasVariants = variants.length > 0;

  if (requestedQuantity <= 0) {
    return {
      isAvailable: false,
      reason: 'invalid_quantity',
      listingId,
      variantSku,
      variantLabel,
      requestedQuantity,
      currentStock: 0,
    };
  }

  if (hasVariants && !variantSku) {
    return {
      isAvailable: false,
      reason: 'variant_required',
      listingId,
      variantSku: null,
      variantLabel,
      requestedQuantity,
      currentStock: 0,
    };
  }

  if (!variantSku) {
    return { isAvailable: true };
  }

  const variant = variants.find(item => item.sku === variantSku);

  if (!variant) {
    return {
      isAvailable: false,
      reason: 'deleted',
      listingId,
      variantSku,
      variantLabel,
      requestedQuantity,
      currentStock: 0,
    };
  }

  if (variant.status !== 'active') {
    return {
      isAvailable: false,
      reason: 'inactive',
      listingId,
      variantSku,
      variantLabel,
      requestedQuantity,
      currentStock: variant.stock,
    };
  }

  if (variant.stock <= 0) {
    return {
      isAvailable: false,
      reason: 'out_of_stock',
      listingId,
      variantSku,
      variantLabel,
      requestedQuantity,
      currentStock: 0,
    };
  }

  if (variant.stock < requestedQuantity) {
    return {
      isAvailable: false,
      reason: 'insufficient_stock',
      listingId,
      variantSku,
      variantLabel,
      requestedQuantity,
      currentStock: variant.stock,
    };
  }

  return { isAvailable: true };
};

export const validateCartStockBeforeCheckout = async (cart, listings = []) => {
  const listingIds = getCartListingIds(cart);
  const listingLookup = (listings || []).reduce((lookup, listing) => {
    const listingId = getListingUuid(listing);
    return listingId ? { ...lookup, [listingId]: listing } : lookup;
  }, {});

  if (!listingIds.length) {
    return { isValid: true, invalidItems: [] };
  }

  const stockStatuses = await Promise.all(
    listingIds.map(async listingId => {
      const cartItem = cart[listingId];
      const response = await getListingVariants(listingId);
      const variants = response?.variants || [];

      const variantStatus = getVariantStockStatus({ listingId, cartItem, variants });
      return variantStatus.isAvailable && !variants.length && !(cartItem?.variantSku || cartItem?.sku)
        ? getPlainListingStockStatus({
          listingId,
          cartItem,
          listing: listingLookup[listingId],
        })
        : variantStatus;
    })
  );

  const invalidItems = stockStatuses.filter(status => !status.isAvailable);

  if (invalidItems.length) {
    throw createCartStockValidationError(invalidItems);
  }

  return { isValid: true, invalidItems: [] };
};

export const initiateOrder = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(initiateOrderRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const {
    deliveryMethod,
    quantity,
    bookingDates,
    cart,
    shippoShippingDetails,
    selectedShippingOption,
    offerPrice,
    paymentIntentData,
    stockValidationListings,
    ...otherOrderParams
  } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const orderData = deliveryMethod ? {
    deliveryMethod,
    selectedShippingOption,
    cart,
    offerPrice,
    paymentIntentData
  } : {
    cart,
    selectedShippingOption,
    offerPrice,
    paymentIntentData,
  };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
  };

  const bodyParams = isTransition
    ? {
      id: transactionId,
      transition: transitionName,
      params: transitionParams,
    }
    : {
      processAlias,
      transition: transitionName,
      params: transitionParams,
    };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSucces = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(initiateOrderSuccess(order));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    return order;
  };

  const handleError = e => {
    dispatch(initiateOrderError(storableError(e)));
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: orderParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    throw e;
  };

  const validateCartStock = () => validateCartStockBeforeCheckout(cart, stockValidationListings);

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return validateCartStock()
      .then(() => transitionPrivileged({ isSpeculative: false, orderData, bodyParams, queryParams }))
      .then(handleSucces)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return validateCartStock()
      .then(() => sdk.transactions.transition(bodyParams, queryParams))
      .then(handleSucces)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return validateCartStock()
      .then(() => initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams }))
      .then(handleSucces)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return validateCartStock()
      .then(() => sdk.transactions.initiate(bodyParams, queryParams))
      .then(handleSucces)
      .catch(handleError);
  }
};

/**
 * Send out-of-stock notification emails to all providers involved in the order.
 *
 * Flow:
 * 1. Extract all listing IDs from the order cart.
 * 2. Send out-of-stock notification for each listing.
 * 3. Continue processing even if some notifications fail.
 * 4. Log success and failure counts for monitoring.
 */
export const sendNotificationToProvidersForListingOutOfStock =
  order => async (dispatch, getState, sdk) => {
    try {
      // Validate order object
      if (!order) {
        throw new Error('Order is required');
      }

      const { id: orderId, attributes = {} } = order;
      const transactionId = getUuidValue(orderId);
      const { protectedData = {} } = attributes;
      const { cart = {} } = protectedData;

      /**
       * Extract listing IDs from cart.
       * Exclude delivery-related keys that are not actual listings.
       */
      const allListingIds = Object.keys(cart).filter(
        key => key !== 'deliveryMethod'
      );

      // Nothing to process
      if (!allListingIds.length) {
        return {
          success: true,
          message: 'No listings found',
          total: 0,
        };
      }

      if (!transactionId) {
        return {
          success: false,
          skipped: true,
          message: 'transactionId is missing',
        };
      }

      /**
       * Send notifications in parallel.
       * Promise.allSettled ensures one failure does not stop
       * notifications for other listings.
       */
      const results = await Promise.allSettled(
        allListingIds.map(async listingId => {
          try {
            return await onSendOutOfStockMailProviders({
              listingId,
              transactionId,
            });
          } catch (error) {
            log.error(error, 'checkout-out-of-stock-notification-listing-failed', {
              listingId,
              transactionId,
            });

            throw error;
          }
        })
      );

      // Calculate summary statistics
      const successCount = results.filter(
        result => result.status === 'fulfilled'
      ).length;

      const failedCount = results.filter(
        result => result.status === 'rejected'
      ).length;

      // Capture failed listing details for debugging
      const failedListings = results
        .map((result, index) => ({
          listingId: allListingIds[index],
          status: result.status,
          reason:
            result.status === 'rejected'
              ? result.reason?.message || result.reason
              : null,
        }))
        .filter(item => item.status === 'rejected');

      return {
        success: true,
        totalListings: allListingIds.length,
        successCount,
        failedCount,
        failedListings,
      };
    } catch (error) {
      log.error(error, 'checkout-out-of-stock-notification-failed', { orderId: order?.id });

      return {
        success: false,
        error:
          error?.message ||
          'Failed to process out-of-stock notifications',
      };
    }
  };

export const confirmPayment = (transactionId, transitionName, transitionParams = {}, listingData, config) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(confirmPaymentRequest());
  const bodyParams = {
    id: transactionId,
    transition: transitionName,
    params: transitionParams,
  };
  const queryParams = {
    include: ['booking', 'provider', 'listing'],
    expand: true,
  };

  return sdk.transactions
    .transition(bodyParams, queryParams)
    .then(async response => {
      const order = response.data.data;
      try {
        const currentUser = getState().user.currentUser;
        const userID = currentUser?.id?.uuid;

        if (order.attributes.processName == PURCHASE_FREE_PROCESS_NAME) {
          // add free purchase entry in mongo
          const mongoData = makeFreeTransactionData(response, currentUser);
          await freeTransactionCreate({ payload: mongoData });
        }
        const userId = order?.relationships?.provider?.data?.id?.uuid || {};
        const customerId = order?.relationships?.customer?.data?.id?.uuid || {};
        // This requires the `search-insights` library

        if (userID) {
          const { id, attributes } = order;
          const { protectedData, payinTotal } = attributes;
          const { cart, childTransactions } = protectedData;
          const allListingIds = Object.keys(cart).filter((st) => st != "deliveryMethod");
          const totalCount = Object.values(cart)
            .filter(item => typeof item === 'object' && item.count !== undefined)
            .reduce((sum, item) => sum + item.count, 0);

          algoliaEventTrackFn(purchasedObjectIDsMethodName, {
            index: process.env.REACT_APP_ALGOLIA_LISTING_INDEX,
            eventName: PURCHASE_COMPLETED_EVENT,
            userToken: userID,
            authenticatedUserToken: userID,
            objectIDs: allListingIds,
            objectData: [
              {
                quantity: totalCount
              }
            ],
            value: payinTotal.amount,
            currency: payinTotal.currency,
            eventData: {
              listingID: allListingIds[0],
              orderID: id.uuid,
              userID,
              timestamp: new Date().toISOString(),
              cart,
              childTransactions
            }
          });

          // transition/accept-offer-provider
          if([
            "transition/confirm-payment",
            "transition/confirm-payment-pickup",
            "transition/confirm-offer-payment",
          ].includes(transitionName)){
            await dispatch(sendNotificationToProvidersForListingOutOfStock(order));
          };

        }
      } catch (e) {
        log.error(e, 'checkout-confirm-payment-side-effects-failed', {
          transactionId: transactionId?.uuid,
          transitionName,
        });
        
      }

      dispatch(confirmPaymentSuccess(order.id));
      setTimeout(() => {
        dispatch(updatedVisitorData(config));
      }, 2000);
      return order;
    })
    .catch(e => {
      dispatch(confirmPaymentError(storableError(e)));
      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
      });
      throw e;
    });
};

export const sendMessage = params => (dispatch, getState, sdk) => {
  const message = params.message;
  const orderId = params.id;

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true });
  }
};

/**
 * Initiate transaction against default-inquiry process
 * Note: At this point inquiry transition is made directly against Marketplace API.
 *       So, client app's server is not involved here unlike with transitions including payments.
 *
 * @param {*} inquiryParams contains listingId and protectedData
 * @param {*} processAlias 'default-inquiry/release-1'
 * @param {*} transitionName 'transition/inquire-without-payment'
 * @returns
 */
export const initiateInquiryWithoutPayment = (inquiryParams, processAlias, transitionName) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateInquiryRequest());

  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(initiateInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const bodyParams = {
    transition: transitionName,
    processAlias,
    params: inquiryParams,
  };
  const queryParams = {
    include: ['provider'],
    expand: true,
  };

  return sdk.transactions
    .initiate(bodyParams, queryParams)
    .then(response => {
      const transactionId = response.data.data.id;
      dispatch(initiateInquirySuccess());
      return transactionId;
    })
    .catch(e => {
      dispatch(initiateInquiryError(storableError(e)));
      throw e;
    });
};

/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */

export const fetchShippingRates = (params) => (dispatch, getState, sdk) => {
  dispatch(fetchRatesRequest());
  return getShippingRates({ ...params }).then((response) => {
    if (response) {
      dispatch(fetchRatesSuccess(response));
      return response;
    }
  })
    .catch(e => {
      dispatch(fetchRatesError(storableError(e)));
    })
};

export const updateUserDeliveryAddress = (params) => (dispatch, getState, sdk) => {
  dispatch(fetchRatesRequest());
  return sdk.currentUser.updateProfile({
    publicData: {
      deliveryAddress: params.deliveryAddress
    }
  }).then(() => {
    dispatch(fetchCurrentUser());
    return dispatch(fetchShippingRates(params.shippoShippingDetails));
  })
};

export const speculateTransaction = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(speculateTransactionRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;
  const {
    deliveryMethod,
    quantity,
    bookingDates,
    cart,
    shippoShippingDetails,
    selectedShippingOption,
    offerPrice,
    stockValidationListings,
    ...otherOrderParams
  } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const orderData = deliveryMethod ? { cart, deliveryMethod, selectedShippingOption, offerPrice } : { cart, selectedShippingOption, offerPrice };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
      id: transactionId,
      transition: transitionName,
      params: transitionParams,
    }
    : {
      processAlias,
      transition: transitionName,
      params: transitionParams,
    };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
    if (deliveryMethod == 'shipping' && shippoShippingDetails) {
      return dispatch(fetchShippingRates(shippoShippingDetails));
    };
  };

  const handleError = e => {
    log.error(e, 'speculate-transaction-failed', {
      listingId: transitionParams.listingId?.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    return dispatch(speculateTransactionError(storableError(e)));
  };

  const validateCartStock = () => validateCartStockBeforeCheckout(cart, stockValidationListings);

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return validateCartStock()
      .then(() => transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams }))
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return validateCartStock()
      .then(() => sdk.transactions.transitionSpeculative(bodyParams, queryParams))
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return validateCartStock()
      .then(() => initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams }))
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return validateCartStock()
      .then(() => sdk.transactions.initiateSpeculative(bodyParams, queryParams))
      .then(handleSuccess)
      .catch(handleError);
  }
};

export const fetchShppingLineItem = (
  orderParams,
  processAlias,
  transitionName,
) => (dispatch, getState, sdk) => {
  dispatch(fetchShippingLineItemRequest());
  const { deliveryMethod, quantity, bookingDates, cart, shippoShippingDetails, selectedShippingOption, ...otherOrderParams } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const orderData = deliveryMethod ? { cart, deliveryMethod, selectedShippingOption } : { cart, selectedShippingOption };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = {
    processAlias,
    transition: transitionName,
    params: transitionParams,
  };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
  };

  const handleError = e => {
    log.error(e, 'speculate-transaction-failed', {
      listingId: transitionParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    return dispatch(speculateTransactionError(storableError(e)));
  };

  return initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
    .then(handleSuccess)
    .catch(handleError);
};

/**
 * Creates individual stock reservation transactions for all the listings in
 * the associated transaction's protectedData.cart, and updates the main
 * transaction protected data with the child transaction ids.
 * @param {*} tx Main cart transaction – expects protectedData.cart
 * @param {*} processAlias Cart stock process alias
 * @param {*} cartItemTransitionName Transition to initiate stock transaction
 * @param {*} parentTransitionName Transition to update the main transaction with child transaction ids
 * @returns 'order', i.e. the updated transaction
 */
export const createStockReservationTransactions = (
  tx,
  processAlias,
  cartItemTransitionName,
  parentTransitionName
) => (dispatch, getState, sdk) => {
  const listingsCart = { ...tx.attributes.protectedData.cart };
  delete listingsCart.deliveryMethod;
  // Create transactions with the cart item process to update stock reservations
  if (listingsCart.length == 0) {
    return tx;
  } else {
    const additionalTransactionFns = Object.keys(listingsCart).map(item => {
      const cartItem = listingsCart[item];
      const count = cartItem.count;
      let stockTransactionId = null;

      // Prepare protected data with variant information if available
      const protectedData = {
        parentTransactionId: tx.id.uuid,
        stockQuantity: count,
      };

      // Add variant metadata if available
      if (cartItem.sku || cartItem.variantSku) {
        protectedData.variantSku = cartItem.sku || cartItem.variantSku;
      }
      if (cartItem.variantLabel) {
        protectedData.variantLabel = cartItem.variantLabel;
      }

      // initiate
      const bodyParams = {
        processAlias: processAlias,
        transition: cartItemTransitionName,
        params: {
          listingId: item,
          stockReservationQuantity: count,
          protectedData,
        },
      };


      return sdk.transactions.initiate(bodyParams, { expand: true }).then(res => {
        stockTransactionId = getUuidValue(res?.data?.data?.id);

        if (!stockTransactionId) {
          throw new Error(`Missing stock transaction id for listing ${item}`);
        }

        // Update variant stock in MongoDB if SKU exists
        if (cartItem.sku || cartItem.variantSku) {
          return updateVariantStock({
            listingId: item,
            transactionId: stockTransactionId,
            variantSku: cartItem.sku || cartItem.variantSku,
            quantity: count,
            operation: 'decrease'
          })
            .then(() => {
              return {
                listingId: item,
                stockTransactionId,
              };
            });
        }

        return {
          listingId: item,
          stockTransactionId,
        };
      });
    });

    return Promise.all(additionalTransactionFns)
      .then(txs => {
        const childTransactions = txs.reduce((children, child) => {
          return {
            ...children,
            [child.listingId]: child.stockTransactionId,
          };
        }, {});

        const parentProtectedData = {
          ...tx.attributes.protectedData,
          childTransactions,
        };

        // Update child transaction ids
        const parentBodyParams = {
          id: tx.id,
          transition: parentTransitionName,
          params: {
            protectedData: parentProtectedData,
          },
        };

        return sdk.transactions.transition(parentBodyParams, { expand: true });
      })
      .then(resp => {
        return resp.data.data;
      });
  }
};

/**
 * Updates the specified transaction's child transactions using the specified transition
 * @param {*} tx Main transaction – expects protectedData.childTransactions
 * @param {*} transitionName Transition to apply to protectedData.childTransactions array
 * @returns 'tx', i.e. the main transaction
 */
export const updateStockReservationTransactions = (tx, transitionName) => (
  dispatch,
  getState,
  sdk
) => {
  const childTransactions = Object.values(tx.attributes.protectedData.childTransactions).filter(Boolean);

  const updateFns = childTransactions.map(childTx => {
    const bodyParams = {
      id: childTx,
      transition: transitionName,
      params: {},
    };

    return sdk.transactions.transition(bodyParams).then(resp => {
      return resp.data;
    });
  });

  return Promise.all(updateFns).then(() => tx);
};


// StripeCustomer is a relantionship to currentUser
// We need to fetch currentUser with correct params to include relationship
export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());
  const fetchCurrentUserOptions = {
    callParams: { include: ['stripeCustomer.defaultPaymentMethod'] },
    updateHasListings: false,
    updateNotifications: false,
  };

  return dispatch(fetchCurrentUser(fetchCurrentUserOptions))
    .then(response => {
      dispatch(stripeCustomerSuccess());
      return response
    })
    .catch(e => {
      dispatch(stripeCustomerError(storableError(e)));
    });
};
